import { useCallback, useEffect, useRef, useState } from 'react';
import { Pencil, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Two ways to sign: draw the signature, or upload a scan of one.
 *
 * Both produce a PNG/JPEG data URL that the backend stores and embeds in the
 * executed PDF — so every signature carries an actual mark. (A typed-name mode
 * existed earlier; it was dropped because a name rendered in a serif face is
 * indistinguishable from the same name typed into the field above it.)
 */

export type SignatureKind = 'drawn' | 'uploaded';

const MODES: Array<{ kind: SignatureKind; label: string; Icon: typeof Pencil }> = [
  { kind: 'drawn', label: 'Draw', Icon: Pencil },
  { kind: 'uploaded', label: 'Upload', Icon: Upload },
];

const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

/** Roomy enough to sign at a natural size — a 60px strip forced a cramped,
 *  unrepresentative scrawl, especially on a trackpad. */
const PAD_HEIGHT = 'h-[168px]';

export default function SignaturePad({
  kind,
  onKindChange,
  onImageChange,
}: {
  kind: SignatureKind;
  onKindChange: (kind: SignatureKind) => void;
  /** PNG/JPEG data URL, or null once the mark is cleared or the mode changes. */
  onImageChange: (dataUrl: string | null) => void;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-4">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.11em] text-muted-foreground">
          Signature
        </span>
        <div className="inline-flex gap-0.5 rounded-lg bg-muted p-0.5">
          {MODES.map(({ kind: value, label, Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                onKindChange(value);
                // Switching modes abandons whatever mark the old one held —
                // otherwise Upload could submit a drawing the user replaced.
                onImageChange(null);
              }}
              className={cn(
                'inline-flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                kind === value
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="size-3" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {kind === 'drawn' ? (
        <DrawnSignature onImageChange={onImageChange} />
      ) : (
        <UploadedSignature onImageChange={onImageChange} />
      )}
    </div>
  );
}

function DrawnSignature({ onImageChange }: { onImageChange: (dataUrl: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Size the backing store to the device pixel ratio, otherwise the stroke is
  // a blurry mess on any retina display. Re-runs on resize because the box is
  // fluid inside a two-column layout.
  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#241F20';
    // A little heavier than before: the pad is taller now, so a 2px line read
    // as thin and scratchy against it.
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    return ctx;
  }, []);

  useEffect(() => {
    fit();
    const onResize = () => {
      // A resize clears the backing store, so the mark is gone either way —
      // tell the parent rather than leaving it holding a stale data URL.
      fit();
      setHasInk(false);
      onImageChange(null);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fit, onImageChange]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const [x, y] = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const [x, y] = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) onImageChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onImageChange(null);
  };

  return (
    <div
      className={cn(
        'relative flex items-center justify-center overflow-hidden rounded-xl border-[1.5px] border-dashed',
        PAD_HEIGHT,
      )}
    >
      <canvas
        ref={canvasRef}
        className="ob-sig-canvas"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        aria-label="Draw your signature"
      />
      {!hasInk && (
        <div className="pointer-events-none flex flex-col items-center gap-1.5 text-muted-foreground">
          <Pencil className="size-5" />
          <span className="text-sm">Draw your signature here</span>
        </div>
      )}
      {/* A baseline to sign on, like a paper form. Sits under the canvas so a
          stroke crossing it stays visible. */}
      <div className="pointer-events-none absolute inset-x-10 bottom-9 h-px bg-border" />
      {hasInk && (
        <button
          type="button"
          onClick={clear}
          className="absolute right-2.5 top-2 cursor-pointer rounded-md border bg-card px-2.5 py-1 text-[11.5px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          Clear
        </button>
      )}
    </div>
  );
}

function UploadedSignature({ onImageChange }: { onImageChange: (dataUrl: string | null) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  const accept = (file: File | undefined) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Use a PNG or JPG image.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('That image is over 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      setError(null);
      setPreview(dataUrl);
      onImageChange(dataUrl);
    };
    reader.onerror = () => setError("That image couldn't be read.");
    reader.readAsDataURL(file);
  };

  return (
    <>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          accept(e.dataTransfer.files?.[0]);
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border-[1.5px] border-dashed text-sm text-muted-foreground transition-colors',
          PAD_HEIGHT,
          over && 'border-secondary bg-secondary/5',
        )}
      >
        {preview ? (
          <img src={preview} alt="Your signature" className="max-h-[140px] object-contain p-3" />
        ) : (
          <>
            <Upload className="size-5" />
            <span>Drop a PNG or JPG of your signature</span>
            <span className="text-xs text-muted-foreground/70">or click to choose a file</span>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        hidden
        onChange={(e) => {
          accept(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
    </>
  );
}
